import {TransportAssoc} from "../transport-assoc";
import {StatisticsBundle, StatisticsBundleWrapper, StatisticsGroup, StatisticsGroupWrapper} from "./statistics-group";
import {TransportServices} from "../transport-services";
import {PipelineFlowTransport} from "../pipeline/pipeline-flow-transport";
import {adjustFromTimeZone, adjustToTimeZone, startOfDay, startOfHour, startOfMonth, startOfWeek} from "../../date/date"
import {TimeStatistics, TimeStatisticsWrapper} from "./time-statistics";
import {StatisticsGroupingWrapper} from "./statistics-grouping";
import * as Constants from "../../util/constants";
import {ObjectCostRateModel} from "../object-cost-rate-model";
import {TruckingCategoryStatisticsWrapper} from "./trucking-statistics";
import {transportCategory} from "../transport-category";
import {atIndexOrUndefined, convertVolumeQuantity, sortMap} from "../../util/util";
import {companyType} from "../company";
import {PipelineCategoryStatisticsWrapper} from "./pipeline-statistics";
import {RawStatistics} from "./raw-statistics";
import {EntityType} from "../entity";
import {toTransportRejectionType} from "../transport-rejection-type";
import {ONE_HOUR_IN_MILLIS} from "../../util/constants";

interface Times {
  regionTime: number
  operatorCompanyTime: number
  truckCompanyTime: number
  vendorCompanyTime: number
  pipelineTime: number
  containerTime: number
  wellTime: number
  siteTime: number
  routeTime: number
  routeGroupTime: number
}

export interface StatisticsBundleBuilderOptions {
  processRegion?: boolean
  processOperatorCompany?: boolean
  processTruckCompany?: boolean
  processVendorCompany?: boolean
  processPipeline?: boolean
  processContainer?: boolean
  processWell?: boolean
  processSite?: boolean
  processRoute?: boolean
  processRouteGroup?: boolean
  volumeUnitOfMeasure?: string
  timeUnitOfMeasure?: string
}

export const DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS: StatisticsBundleBuilderOptions = {
  processRegion: true,
  processOperatorCompany: true,
  processTruckCompany: true,
  processVendorCompany: true,
  processPipeline: true,
  processContainer: false,
  processWell: false,
  processSite: true,
  processRoute: true,
  processRouteGroup: true,
  volumeUnitOfMeasure: "barrels",
  timeUnitOfMeasure: "hours",
}

export const EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS: StatisticsBundleBuilderOptions = {
  processRegion: false,
  processOperatorCompany: false,
  processTruckCompany: false,
  processVendorCompany: false,
  processPipeline: false,
  processContainer: false,
  processWell: false,
  processSite: false,
  processRoute: false,
  processRouteGroup: false,
  volumeUnitOfMeasure: DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.volumeUnitOfMeasure,
  timeUnitOfMeasure: DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS.timeUnitOfMeasure,
}

interface SortAndCalcOptions {
  id: string
  name: string
  entityType: EntityType,
  pickup?: boolean
  calcSubTruckCompanies?: boolean
  calcSubPipelines?: boolean
}

export class StatisticsBundleBuilder {
  volumeUnitOfMeasure
  timeUnitOfMeasure

  private sortingOperatorCompanyMap = new Map<string, StatisticsGroupWrapper>();
  private sortingTruckCompanyMap = new Map<string, StatisticsGroupWrapper>();
  private sortingDisposalCompanyMap = new Map<string, StatisticsGroupWrapper>();
  private sortingRegionMap = new Map<string, StatisticsGroupWrapper>();
  private sortingContainerMap = new Map<string, StatisticsGroupWrapper>();
  private sortingDeliveryContainerMap = new Map<string, StatisticsGroupWrapper>();
  private sortingSiteMap = new Map<string, StatisticsGroupWrapper>();
  private sortingPickupSiteMap = new Map<string, StatisticsGroupWrapper>();
  private sortingDeliverySiteMap = new Map<string, StatisticsGroupWrapper>();
  private sortingWellMap = new Map<string, StatisticsGroupWrapper>();
  private sortingPipelineMap = new Map<string, StatisticsGroupWrapper>();
  private sortingRouteMap = new Map<string, StatisticsGroupWrapper>();
  private sortingPickupRouteMap = new Map<string, StatisticsGroupWrapper>();
  private sortingDeliveryRouteMap = new Map<string, StatisticsGroupWrapper>();
  private sortingRouteGroupMap = new Map<string, StatisticsGroupWrapper>();
  private sortingPickupRouteGroupMap = new Map<string, StatisticsGroupWrapper>();
  private sortingDeliveryRouteGroupMap = new Map<string, StatisticsGroupWrapper>();

  private processedTtaIds = new Set<string>();

  private constructor(private options: StatisticsBundleBuilderOptions) {
    this.volumeUnitOfMeasure = options.volumeUnitOfMeasure ?? "barrels"
    this.timeUnitOfMeasure = options.timeUnitOfMeasure ?? "hours"
  }

  static build = (rawStats: RawStatistics | undefined, options = DEFAULT_STATISTICS_BUNDLE_BUILDER_OPTIONS): StatisticsBundleWrapper => {
    return new StatisticsBundleBuilder(options).build(rawStats)
  }

  static buildFromPlain = (plain: StatisticsBundle): StatisticsBundleWrapper => {
    const wrapper = new StatisticsBundleWrapper()

    plain.regionStats.forEach((value) => {
      wrapper.regionStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.truckCompanyStats.forEach((value) => {
      wrapper.truckCompanyStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.operatorStats.forEach((value) => {
      wrapper.operatorStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.disposalStats.forEach((value) => {
      wrapper.disposalStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.pipelineStats.forEach((value) => {
      wrapper.pipelineStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.containerStats.forEach((value) => {
      wrapper.containerStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.wellStats.forEach((value) => {
      wrapper.wellStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.siteStats.forEach((value) => {
      wrapper.siteStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.pickupSiteStats.forEach((value) => {
      wrapper.pickupSiteStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.deliverySiteStats.forEach((value) => {
      wrapper.deliverySiteStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.routeStats.forEach((value) => {
      wrapper.routeStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.pickupRouteStats.forEach((value) => {
      wrapper.pickupRouteStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.deliveryRouteStats.forEach((value) => {
      wrapper.deliveryRouteStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.routeGroupStats.forEach((value) => {
      wrapper.routeGroupStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.pickupRouteGroupStats.forEach((value) => {
      wrapper.pickupRouteGroupStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    plain.deliveryRouteGroupStats.forEach((value) => {
      wrapper.deliveryRouteGroupStats.push(StatisticsBundleBuilder.toStatisticsGroupWrapper(value))
    })

    return wrapper
  }

  private static toTimeStatisticsWrapper = (plain: TimeStatistics): TimeStatisticsWrapper => {
    const timeWrapper = new TimeStatisticsWrapper(plain.dttm)
    timeWrapper.transportServicesList.push(...plain.transportServicesList)

    Object.assign(timeWrapper.trucking.water, plain.trucking.water)
    Object.assign(timeWrapper.trucking.oil, plain.trucking.oil)
    Object.assign(timeWrapper.trucking.other, plain.trucking.other)

    Object.assign(timeWrapper.pipeline.water, plain.pipeline.water)
    Object.assign(timeWrapper.pipeline.oil, plain.pipeline.oil)
    Object.assign(timeWrapper.pipeline.other, plain.pipeline.other)

    Object.assign(timeWrapper.inventory.water, plain.inventory.water)
    Object.assign(timeWrapper.inventory.oil, plain.inventory.oil)
    Object.assign(timeWrapper.inventory.other, plain.inventory.other)

    return timeWrapper
  }

  private static toStatisticsGroupWrapper = (plain: StatisticsGroup): StatisticsGroupWrapper => {
    const groupWrapper = new StatisticsGroupWrapper(plain.id, plain.name, plain.entityType)
    groupWrapper.isPickup = plain.isPickup
    groupWrapper.uom = plain.uom
    groupWrapper.timeUom = plain.timeUom

    Object.keys(plain.hour)
      .map(Number)
      .forEach((key) => {
        groupWrapper.hour[key] = StatisticsBundleBuilder.toTimeStatisticsWrapper(plain.hour[key])
      })

    Object.keys(plain.day)
      .map(Number)
      .forEach((key) => {
        groupWrapper.day[key] = StatisticsBundleBuilder.toTimeStatisticsWrapper(plain.day[key])
      })

    Object.keys(plain.week)
      .map(Number)
      .forEach((key) => {
        groupWrapper.week[key] = StatisticsBundleBuilder.toTimeStatisticsWrapper(plain.week[key])
      })

    Object.keys(plain.month)
      .map(Number)
      .forEach((key) => {
        groupWrapper.month[key] = StatisticsBundleBuilder.toTimeStatisticsWrapper(plain.month[key])
      })

    return groupWrapper
  }

  private build = (rawStats: RawStatistics | undefined): StatisticsBundleWrapper => {
    console.time("Processing Time")
    const times: Times = {
      regionTime: 0,
      operatorCompanyTime: 0,
      truckCompanyTime: 0,
      vendorCompanyTime: 0,
      pipelineTime: 0,
      containerTime: 0,
      wellTime: 0,
      siteTime: 0,
      routeTime: 0,
      routeGroupTime: 0,
    }
    if (rawStats) {
      rawStats.ttaList?.forEach(tta => this.processTta(tta, times))
      rawStats.flowTransports?.forEach(this.processFlow)
    }

    console.log("regionTime=" + times.regionTime)
    console.log("operatorCompanyTime=" + times.operatorCompanyTime)
    console.log("truckCompanyTime=" + times.truckCompanyTime)
    console.log("vendorCompanyTime=" + times.vendorCompanyTime)
    console.log("pipelineTime=" + times.pipelineTime)
    console.log("containerTime=" + times.containerTime)
    console.log("wellTime=" + times.wellTime)
    console.log("siteTime=" + times.siteTime)
    console.log("routeTime=" + times.routeTime)
    console.log("routeGroupTime=" + times.routeGroupTime)

    console.timeEnd("Processing Time")

    return this.buildBundle()
  }

  private toArray = (map:  Map<string, StatisticsGroupWrapper>) : StatisticsGroupWrapper[] => {
    // clear out processing data
    map.forEach(value => value.processedTransportIds.clear())

    // sort
    return [...(sortMap(map).values())]
  }

  private buildBundle = (): StatisticsBundleWrapper => {
    const bundle = new StatisticsBundleWrapper()
    bundle.regionStats.push(...(this.toArray(this.sortingRegionMap)))
    bundle.containerStats.push(...(this.toArray(this.sortingContainerMap)))
    bundle.siteStats.push(...(this.toArray(this.sortingSiteMap)))
    bundle.pickupSiteStats.push(...(this.toArray(this.sortingPickupSiteMap)))
    bundle.deliverySiteStats.push(...(this.toArray(this.sortingDeliverySiteMap)))
    bundle.wellStats.push(...(this.toArray(this.sortingWellMap)))
    bundle.pipelineStats.push(...(this.toArray(this.sortingPipelineMap)))
    bundle.truckCompanyStats.push(...(this.toArray(this.sortingTruckCompanyMap)))
    bundle.operatorStats.push(...(this.toArray(this.sortingOperatorCompanyMap)))
    bundle.disposalStats.push(...(this.toArray(this.sortingDisposalCompanyMap)))
    bundle.routeStats.push(...(this.toArray(this.sortingRouteMap)))
    bundle.pickupRouteStats.push(...(this.toArray(this.sortingPickupRouteMap)))
    bundle.deliveryRouteStats.push(...(this.toArray(this.sortingDeliveryRouteMap)))
    bundle.routeGroupStats.push(...(this.toArray(this.sortingRouteGroupMap)))
    bundle.pickupRouteGroupStats.push(...(this.toArray(this.sortingPickupRouteGroupMap)))
    bundle.deliveryRouteGroupStats.push(...(this.toArray(this.sortingDeliveryRouteGroupMap)))
    return bundle
  }

  private processTta = (tta: TransportAssoc, times: Times) => {
    let start = new Date().getTime()
    if (this.options.processRegion) {
      this.sortTtaIntoRegion(tta)
    }
    times.regionTime += new Date().getTime() - start

    if (tta.pickupTransport) {
      start = new Date().getTime()
      if (this.options.processContainer) {
        this.sortTtaIntoPickupContainer(tta)
      }
      times.containerTime += new Date().getTime() - start
      start = new Date().getTime()
      if (this.options.processSite) {
        this.sortTtaIntoSite(tta, true)
        this.sortTtaIntoPickupSite(tta)
      }
      times.siteTime += new Date().getTime() - start
      start = new Date().getTime()
      if (this.options.processWell) {
        this.sortTtaIntoWell(tta, true)
      }
      times.wellTime += new Date().getTime() - start
      start = new Date().getTime()
      if (this.options.processRoute) {
        this.sortTtaIntoRoute(tta, true)
        this.sortTtaIntoPickupRoute(tta)
      }
      times.routeTime += new Date().getTime() - start
      start = new Date().getTime()
      if (this.options.processRouteGroup) {
        this.sortTtaIntoRouteGroup(tta, true)
        this.sortTtaIntoPickupRouteGroup(tta)
      }
      times.routeGroupTime += new Date().getTime() - start
      this.processedTtaIds.add(tta.id)
    }

    if (tta.deliveryTransport) {
      start = new Date().getTime()
      if (this.options.processContainer) {
        this.sortTtaIntoDeliveryContainer(tta)
      }
      times.containerTime += new Date().getTime() - start
      start = new Date().getTime()
      if (this.options.processSite) {
        this.sortTtaIntoDeliverySite(tta)
      }
      times.siteTime += new Date().getTime() - start
      start = new Date().getTime()
      if (this.options.processRoute) {
        this.sortTtaIntoDeliveryRoute(tta)
      }
      times.routeTime += new Date().getTime() - start
      start = new Date().getTime()
      if (this.options.processRouteGroup) {
        this.sortTtaIntoDeliveryRouteGroup(tta)
      }
      times.routeGroupTime += new Date().getTime() - start
      if (!this.processedTtaIds.has(tta.id)) {
        start = new Date().getTime()
        if (this.options.processSite) {
          this.sortTtaIntoSite(tta, false)
        }
        times.siteTime += new Date().getTime() - start
        start = new Date().getTime()
        if (this.options.processWell) {
          this.sortTtaIntoWell(tta, false)
        }
        times.wellTime += new Date().getTime() - start
        start = new Date().getTime()
        if (this.options.processRoute) {
          this.sortTtaIntoRoute(tta, false)
        }
        times.routeTime += new Date().getTime() - start
        start = new Date().getTime()
        if (this.options.processRouteGroup) {
          this.sortTtaIntoRouteGroup(tta, false)
        }
        times.routeGroupTime += new Date().getTime() - start
      }
      this.processedTtaIds.add(tta.id)
    }
    start = new Date().getTime()
    this.sortTtaIntoCompany(tta)
    times.operatorCompanyTime += new Date().getTime() - start
    times.vendorCompanyTime += new Date().getTime() - start
    times.truckCompanyTime += new Date().getTime() - start
  }

  private processFlow = (flow: PipelineFlowTransport) => {
    if (this.options.processRegion) {
      this.sortFlowIntoRegion(flow)
    }
    if (this.options.processSite) {
      this.sortFlowIntoSite(flow)
      this.sortFlowIntoPickupSite(flow)
      this.sortFlowIntoDeliverySite(flow)
    }
    if (this.options.processWell) {
      this.sortFlowIntoWell(flow)
    }
    if (this.options.processRoute) {
      this.sortFlowIntoRoute(flow)
      this.sortFlowIntoPickupRoute(flow)
      this.sortFlowIntoDeliveryRoute(flow)
    }
    if (this.options.processRouteGroup) {
      this.sortFlowIntoRouteGroup(flow)
      this.sortFlowIntoPickupRouteGroup(flow)
      this.sortFlowIntoDeliveryRouteGroup(flow)
    }
    if (this.options.processPipeline) {
      this.sortFlowIntoPipeline(flow)
    }
    this.sortFlowIntoCompany(flow)
  }

  private sortTtaIntoRegion = (tta: TransportAssoc) => {
    const id = tta.pickupTransport?.regionId ?? tta.deliveryTransport?.regionId
    const name = tta.pickupTransport?.regionGeographicName ?? tta.deliveryTransport?.regionGeographicName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "REGION", pickup: true}
      this.sortTtaAndCalculate(tta, this.sortingRegionMap, options)
    }
  }

  private sortFlowIntoRegion = (flow: PipelineFlowTransport) => {
    const id = flow.regionId
    const name = flow.regionGeographicName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "REGION", pickup: true}
      this.sortFlowAndCalculate(flow, this.sortingRegionMap, options)
    }
  }

  private sortTtaIntoPickupContainer = (tta: TransportAssoc) => {
    const id = tta.pickupTransport?.containerId
    const name = tta.pickupTransport?.containerName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "CONTAINER", pickup: true}
      this.sortTtaAndCalculate(tta, this.sortingContainerMap, options)
    }
  }

  private sortTtaIntoDeliveryContainer = (tta: TransportAssoc) => {
    const id = tta.deliveryTransport?.containerId
    const name = tta.deliveryTransport?.containerName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "CONTAINER", pickup: false}
      this.sortTtaAndCalculate(tta, this.sortingDeliveryContainerMap, options)
    }
  }

  private sortTtaIntoSite = (tta: TransportAssoc, pickup: boolean) => {
    const transport = pickup ? tta.pickupTransport : tta.deliveryTransport
    const id = transport?.siteId
    const name = transport?.siteName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "SITE", pickup: pickup}
      this.sortTtaAndCalculate(tta, this.sortingSiteMap, options)
    }
  }

  private sortFlowIntoSite = (flow: PipelineFlowTransport) => {
    let id = flow.sourceSiteId
    let name = flow.sourceSiteName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "SITE", pickup: true}
      this.sortFlowAndCalculate(flow, this.sortingSiteMap, options)
    }

    id = flow.destinationSiteId
    name = flow.destinationSiteName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "SITE", pickup: false}
      this.sortFlowAndCalculate(flow, this.sortingSiteMap, options)
    }
  }

  private sortTtaIntoPickupSite = (tta: TransportAssoc) => {
    const id = tta.pickupTransport?.siteId
    const name = tta.pickupTransport?.siteName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "SITE", pickup: true}
      this.sortTtaAndCalculate(tta, this.sortingPickupSiteMap, options)
    }
  }

  private sortFlowIntoPickupSite = (flow: PipelineFlowTransport) => {
    const id = flow.sourceSiteId
    const name = flow.sourceSiteName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "SITE", pickup: true}
      this.sortFlowAndCalculate(flow, this.sortingPickupSiteMap, options)
    }
  }

  private sortTtaIntoDeliverySite = (tta: TransportAssoc) => {
    const id = tta.deliveryTransport?.siteId
    const name = tta.deliveryTransport?.siteName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "SITE", pickup: false}
      this.sortTtaAndCalculate(tta, this.sortingDeliverySiteMap, options)
    }
  }

  private sortFlowIntoDeliverySite = (flow: PipelineFlowTransport) => {
    const id = flow.destinationSiteId
    const name = flow.destinationSiteName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "SITE", pickup: false}
      this.sortFlowAndCalculate(flow, this.sortingDeliverySiteMap, options)
    }
  }

  private sortTtaIntoWell = (tta: TransportAssoc, pickup: boolean) => {
    const transport = pickup ? tta.pickupTransport : tta.deliveryTransport
    const id = transport?.wellId
    const name = transport?.wellName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "WELL", pickup: pickup}
      this.sortTtaAndCalculate(tta, this.sortingWellMap, options)
    }
  }

  private sortFlowIntoWell = (flow: PipelineFlowTransport) => {
    let id = flow.sourceWellId
    let name = flow.sourceWellName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "WELL", pickup: true}
      this.sortFlowAndCalculate(flow, this.sortingWellMap, options)
    }

    id = flow.destinationWellId
    name = flow.destinationWellName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "WELL", pickup: false}
      this.sortFlowAndCalculate(flow, this.sortingWellMap, options)
    }
  }

  private sortTtaIntoRoute = (tta: TransportAssoc, pickup: boolean) => {
    const transport = pickup ? tta.pickupTransport : tta.deliveryTransport
    if (transport?.routeIds && transport.routeIds.length > 0) {
      transport.routeIds.forEach((id, index) => {
        const name = atIndexOrUndefined(transport.routeNames, index)
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTE", pickup: pickup}
          this.sortTtaAndCalculate(tta, this.sortingRouteMap, options)
        }
      })
    } else {
      const options: SortAndCalcOptions = {id: "-1", name: "(No Route)", entityType: "ROUTE", pickup: pickup}
      this.sortTtaAndCalculate(tta, this.sortingRouteMap, options)
    }
  }

  private sortFlowIntoRoute = (flow: PipelineFlowTransport) => {
    if (flow?.sourceRoutes) {
      flow.sourceRoutes.forEach((route) => {
        const id = route.id
        const name = route.name
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTE", pickup: true}
          this.sortFlowAndCalculate(flow, this.sortingRouteMap, options)
        }
      })
    }

    if (flow?.destinationRoutes) {
      flow.destinationRoutes.forEach((route) => {
        const id = route.id
        const name = route.name
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTE", pickup: false}
          this.sortFlowAndCalculate(flow, this.sortingRouteMap, options)
        }
      })
    }
  }

  private sortTtaIntoPickupRoute = (tta: TransportAssoc) => {
    const transport = tta.pickupTransport
    if (transport?.routeIds && transport.routeIds.length > 0) {
      transport.routeIds.forEach((id, index) => {
        const name = atIndexOrUndefined(transport.routeNames, index)
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTE", pickup: true}
          this.sortTtaAndCalculate(tta, this.sortingPickupRouteMap, options)
        }
      })
    } else {
      const options: SortAndCalcOptions = {id: "-1", name: "(No Route)", entityType: "ROUTE", pickup: true}
      this.sortTtaAndCalculate(tta, this.sortingPickupRouteMap, options)
    }
  }

  private sortFlowIntoPickupRoute = (flow: PipelineFlowTransport) => {
    if (flow?.sourceRoutes) {
      flow.sourceRoutes.forEach((route) => {
        const id = route.id
        const name = route.name
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTE", pickup: true}
          this.sortFlowAndCalculate(flow, this.sortingPickupRouteMap, options)
        }
      })
    }
  }

  private sortTtaIntoDeliveryRoute = (tta: TransportAssoc) => {
    const transport = tta.deliveryTransport
    if (transport?.routeIds && transport.routeIds.length > 0) {
      transport.routeIds.forEach((id, index) => {
        const name = atIndexOrUndefined(transport.routeNames, index)
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTE", pickup: false}
          this.sortTtaAndCalculate(tta, this.sortingDeliveryRouteMap, options)
        }
      })
    } else {
      const options: SortAndCalcOptions = {id: "-1", name: "(No Route)", entityType: "ROUTE", pickup: false}
      this.sortTtaAndCalculate(tta, this.sortingDeliveryRouteMap, options)
    }
  }

  private sortFlowIntoDeliveryRoute = (flow: PipelineFlowTransport) => {
    if (flow?.destinationRoutes) {
      flow.destinationRoutes.forEach((route) => {
        const id = route.id
        const name = route.name
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTE", pickup: false}
          this.sortFlowAndCalculate(flow, this.sortingDeliveryRouteMap, options)
        }
      })
    }
  }

  private sortTtaIntoRouteGroup = (tta: TransportAssoc, pickup: boolean) => {
    const transport = pickup ? tta.pickupTransport : tta.deliveryTransport
    if (transport?.routeGroupIds && transport.routeGroupIds.length > 0) {
      transport.routeGroupIds.forEach((id, index) => {
        const name = atIndexOrUndefined(transport.routeGroupNames, index)
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTEGROUP", pickup: pickup}
          this.sortTtaAndCalculate(tta, this.sortingRouteGroupMap, options)
        }
      })
    } else {
      const options: SortAndCalcOptions = {id: "-1", name: "(No Route Group)", entityType: "ROUTEGROUP", pickup: pickup}
      this.sortTtaAndCalculate(tta, this.sortingRouteGroupMap, options)
    }
  }

  private sortFlowIntoRouteGroup = (flow: PipelineFlowTransport) => {
    if (flow?.sourceRoutes) {
      flow.sourceRoutes.forEach((route) => {
        const id = route.routeGroupId
        const name = route.routeGroupName
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTEGROUP", pickup: true}
          this.sortFlowAndCalculate(flow, this.sortingRouteGroupMap, options)
        }
      })
    }

    if (flow?.destinationRoutes) {
      flow.destinationRoutes.forEach((route) => {
        const id = route.routeGroupId
        const name = route.routeGroupName
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTEGROUP", pickup: false}
          this.sortFlowAndCalculate(flow, this.sortingRouteGroupMap, options)
        }
      })
    }
  }

  private sortTtaIntoPickupRouteGroup = (tta: TransportAssoc) => {
    const transport = tta.pickupTransport
    if (transport?.routeGroupIds && transport.routeGroupIds.length > 0) {
      transport.routeGroupIds.forEach((id, index) => {
        const name = atIndexOrUndefined(transport.routeGroupNames, index)
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTEGROUP", pickup: true}
          this.sortTtaAndCalculate(tta, this.sortingPickupRouteGroupMap, options)
        }
      })
    } else {
      const options: SortAndCalcOptions = {id: "-1", name: "(No Route Group)", entityType: "ROUTEGROUP", pickup: true}
      this.sortTtaAndCalculate(tta, this.sortingPickupRouteGroupMap, options)
    }
  }

  private sortFlowIntoPickupRouteGroup = (flow: PipelineFlowTransport) => {
    if (flow?.sourceRoutes) {
      flow.sourceRoutes.forEach((route) => {
        const id = route.routeGroupId
        const name = route.routeGroupName
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTEGROUP", pickup: true}
          this.sortFlowAndCalculate(flow, this.sortingPickupRouteGroupMap, options)
        }
      })
    }
  }

  private sortTtaIntoDeliveryRouteGroup = (tta: TransportAssoc) => {
    const transport = tta.deliveryTransport
    if (transport?.routeGroupIds && transport.routeGroupIds.length > 0) {
      transport.routeGroupIds.forEach((id, index) => {
        const name = atIndexOrUndefined(transport.routeGroupNames, index)
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTEGROUP", pickup: false}
          this.sortTtaAndCalculate(tta, this.sortingDeliveryRouteGroupMap, options)
        }
      })
    } else {
      const options: SortAndCalcOptions = {id: "-1", name: "(No Route Group)", entityType: "ROUTEGROUP", pickup: false}
      this.sortTtaAndCalculate(tta, this.sortingDeliveryRouteGroupMap, options)
    }
  }

  private sortFlowIntoDeliveryRouteGroup = (flow: PipelineFlowTransport) => {
    if (flow?.destinationRoutes) {
      flow.destinationRoutes.forEach((route) => {
        const id = route.routeGroupId
        const name = route.routeGroupName
        if (id && name) {
          const options: SortAndCalcOptions = {id: id, name: name, entityType: "ROUTEGROUP", pickup: false}
          this.sortFlowAndCalculate(flow, this.sortingDeliveryRouteGroupMap, options)
        }
      })
    }
  }

  private sortFlowIntoPipeline = (flow: PipelineFlowTransport) => {
    const id = flow.pipelineId
    const name = flow.pipelineName

    if (id && name) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "PIPE", pickup: true}
      this.sortFlowAndCalculate(flow, this.sortingPipelineMap, options)
    }
  }

  private sortTtaIntoCompany = (tta: TransportAssoc) => {
    let company = this.getTtaTruckCompany(tta)
    let id = company?.id
    let name = company?.name

    if (id && name && this.options.processTruckCompany) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "COMPANY", pickup: true}
      this.sortTtaAndCalculate(tta, this.sortingTruckCompanyMap, options)
    }

    company = this.getTtaVendorCompany(tta)
    id = company?.id
    name = company?.name

    if (id && name && this.options.processVendorCompany) {
      const options: SortAndCalcOptions = {
        id: id,
        name: name,
        entityType: "COMPANY",
        pickup: false,
        calcSubTruckCompanies: true,
      }
      this.sortTtaAndCalculate(tta, this.sortingDisposalCompanyMap, options)
    }

    company = this.getTtaOperatorCompany(tta)
    id = company?.id
    name = company?.name

    if (id && name && this.options.processOperatorCompany) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "COMPANY", pickup: true}
      this.sortTtaAndCalculate(tta, this.sortingOperatorCompanyMap, options)
    }
  }

  private sortFlowIntoCompany = (flow: PipelineFlowTransport) => {
    let company = this.getFlowVendorCompany(flow)
    let id = company?.id
    let name = company?.name

    if (id && name && this.options.processVendorCompany) {
      const options: SortAndCalcOptions = {
        id: id,
        name: name,
        entityType: "COMPANY",
        pickup: false,
        calcSubPipelines: true,
      }
      this.sortFlowAndCalculate(flow, this.sortingDisposalCompanyMap, options)
    }

    company = this.getFlowOperatorCompany(flow)
    id = company?.id
    name = company?.name

    if (id && name && this.options.processOperatorCompany) {
      const options: SortAndCalcOptions = {id: id, name: name, entityType: "COMPANY", pickup: true}
      this.sortFlowAndCalculate(flow, this.sortingOperatorCompanyMap, options)
    }
  }

  private sortTtaAndCalculate = (tta: TransportAssoc, sortMap: Map<string, StatisticsGroupWrapper>, options: SortAndCalcOptions) => {
    const grouping = this.getOrComputeGrouping(sortMap, options)
    const ts = TransportServices.fromTta(tta)
    this.calculateStats(ts, grouping, options)
  }

  private sortFlowAndCalculate = (flow: PipelineFlowTransport, sortMap: Map<string, StatisticsGroupWrapper>, options: SortAndCalcOptions) => {
    const grouping = this.getOrComputeGrouping(sortMap, options)
    const ts = TransportServices.fromFlow(flow)
    this.calculateStats(ts, grouping, options)
  }

  private calculateStats = (ts: TransportServices, grouping: StatisticsGroupWrapper, options: SortAndCalcOptions) => {
    if (ts.transportAssoc) {
      this.calculateTtaStats(ts, ts.transportAssoc, grouping, options)
    } else if (ts.pipelineFlowTransport) {
      this.calculateFlowStats(ts, ts.pipelineFlowTransport, grouping, options)
    }
  }

  private calculateTtaStats = (ts: TransportServices, tta: TransportAssoc, grouping: StatisticsGroupWrapper, options: SortAndCalcOptions) => {
    const isPickup = grouping.isPickup

    this.calculateTtaGroupingStats(ts, tta, grouping, isPickup)

    const pickup = tta.pickupTransport
    const delivery = tta.deliveryTransport

    if (pickup && delivery) {
      const transport = isPickup ? delivery : pickup
      if (transport.siteId && transport.siteName) {
        const reverseOptions: SortAndCalcOptions = {id: transport.siteId, name: transport.siteName, entityType: "SITE"}
        const reverseGrouping = this.getOrComputeGroupingRecord(grouping.reverseSiteStatMap, reverseOptions)
        this.calculateTtaGroupingStats(ts, tta, reverseGrouping, !isPickup)
      }

      if (options.calcSubTruckCompanies) {
        const company = this.getTtaTruckCompany(tta)
        const companyId = company?.id
        const companyName = company?.name

        if (companyId && companyName) {
          let subOptions: SortAndCalcOptions = {
            id: companyId,
            name: companyName,
            entityType: "COMPANY",
            pickup: isPickup
          }
          let subGrouping = this.getOrComputeGroupingRecord(grouping.truckCompanyMap, subOptions)
          this.calculateTtaGroupingStats(ts, tta, subGrouping, isPickup)

          const transport = isPickup ? pickup : delivery
          if (transport.siteId && transport.siteName) {
            subOptions = {
              id: transport.siteId,
              name: transport.siteName,
              entityType: "SITE",
              pickup: isPickup
            }
            subGrouping = this.getOrComputeGroupingRecord(subGrouping.siteMap, subOptions)
            this.calculateTtaGroupingStats(ts, tta, subGrouping, isPickup)
          }
        }
      }
    }

    const dttm = isPickup ? pickup?.createDttm : delivery?.createDttm
    if (dttm) {
      const hourDttm = adjustToTimeZone(startOfHour(adjustFromTimeZone(dttm))).getTime()
      const dayDttm = adjustToTimeZone(startOfDay(adjustFromTimeZone(dttm))).getTime()
      const weekDttm = adjustToTimeZone(startOfWeek(adjustFromTimeZone(dttm))).getTime()
      const monthDttm = adjustToTimeZone(startOfMonth(adjustFromTimeZone(dttm))).getTime()

      let timeStats: TimeStatisticsWrapper = this.getOrComputeTimeGrouping(hourDttm, grouping.hour)
      this.calculateTtaGroupingStats(ts, tta, timeStats, isPickup)

      timeStats = this.getOrComputeTimeGrouping(dayDttm, grouping.day)
      this.calculateTtaGroupingStats(ts, tta, timeStats, isPickup)

      timeStats = this.getOrComputeTimeGrouping(weekDttm, grouping.week)
      this.calculateTtaGroupingStats(ts, tta, timeStats, isPickup)

      timeStats = this.getOrComputeTimeGrouping(monthDttm, grouping.month)
      this.calculateTtaGroupingStats(ts, tta, timeStats, isPickup)
    }
  }

  private calculateTtaGroupingStats = (ts: TransportServices, tta: TransportAssoc, grouping: StatisticsGroupingWrapper, pickup: boolean) => {
    let categoryStats: TruckingCategoryStatisticsWrapper = grouping.trucking.other
    if (tta.category) {
      if (tta.category === transportCategory.water.code) {
        categoryStats = grouping.trucking.water
      } else if (tta.category === transportCategory.oil.code) {
        categoryStats = grouping.trucking.oil
      }
    }

    // increment record count
    categoryStats.recordCount++;

    const rejectionTypeCode = tta.pickupTransport?.rejectionType

    // increment load count
    if (rejectionTypeCode) {
      const rejectionType = toTransportRejectionType(rejectionTypeCode)
      if (rejectionType.code in categoryStats.rejectsByType) {
        categoryStats.rejectsByType[rejectionType.code] = categoryStats.rejectsByType[rejectionType.code] + 1
      } else {
        categoryStats.rejectsByType[rejectionType.code] = 1
      }
      categoryStats.totalRejects++;
    } else {
      categoryStats.totalLoads++;
    }

    // turn around time
    const turnAroundTime = tta.turnAroundTime;
    if (turnAroundTime) {
      categoryStats.havingTurnaroundTime++;
      categoryStats.totalTurnaroundTime += (turnAroundTime / ONE_HOUR_IN_MILLIS);
    }

    const transport = pickup ? tta.pickupTransport : tta.deliveryTransport
    if (transport && !grouping.processedTransportIds.has(transport.id)) {
      // transit time
      const transitTime = tta.turnAroundTime;
      if (transitTime) {
        categoryStats.havingTravelTime++;
        categoryStats.totalTravelTime += (transitTime / ONE_HOUR_IN_MILLIS);
      }

      // on site time
      const inDttm = transport.inDttm;
      const outDttm = transport.outDttm;
      if (inDttm && outDttm) {
        const onSiteTime = outDttm - inDttm;
        if (onSiteTime >= Constants.FIVE_MINUTES_IN_MILLIS) {
          categoryStats.havingOnSiteTime++;
          categoryStats.totalOnSiteTime += (onSiteTime / ONE_HOUR_IN_MILLIS);
        }
      }

      // quantity
      let quantity = transport.quantityDecimal
      const uom: string | undefined = transport.quantityUnitOfMeasure;
      if (quantity && uom) {
        const convertedQuantity = convertVolumeQuantity(
          uom,
          this.volumeUnitOfMeasure,
          quantity
        );
        if (convertedQuantity) {
          categoryStats.totalQty += convertedQuantity;
        }
      }

      // standard quantity
      quantity = transport.standardQuantity ?? transport.quantityDecimal
      if (quantity && uom) {
        const convertedQuantity = convertVolumeQuantity(
          uom,
          this.volumeUnitOfMeasure,
          quantity
        );
        if (convertedQuantity) {
          categoryStats.totalStandardQty += convertedQuantity;
        }
      }

      // net quantity
      quantity = transport.netQuantity ?? transport.quantityDecimal
      if (quantity && uom) {
        const convertedQuantity = convertVolumeQuantity(
          uom,
          this.volumeUnitOfMeasure,
          quantity
        );
        if (convertedQuantity) {
          categoryStats.totalNetQty += convertedQuantity;
        }
      }

      // last activity dates
      const recordDttm = transport.createDttm;
      if (recordDttm) {
        const lastActivityDttm = categoryStats.lastActivityDttm;
        if (!lastActivityDttm || recordDttm > lastActivityDttm) {
          categoryStats.lastActivityDttm = recordDttm;
        }
      }

      grouping.processedTransportIds.add(transport.id)
    }

    // cost
    if (tta.objectCostRateModelList) {
      tta.objectCostRateModelList.forEach((ocrm) => {
        if (this.shouldAddCost(ocrm)) {
          categoryStats.addOcrmCost(ocrm);
        }
      })
    }

    if (tta.warnings) {
      tta.warnings.forEach((warning) => {
        if (warning.type === 'TTA_DELIVERY_TO_NON_PRIMARY_SITE') {
          categoryStats.deliveredToNonPrimarySiteCount++
        }
      })
    }
  }

  private calculateFlowStats = (ts: TransportServices, flow: PipelineFlowTransport, grouping: StatisticsGroupWrapper, options: SortAndCalcOptions) => {
    this.calculateFlowGroupingStats(ts, flow, grouping)

    const siteId = grouping.isPickup ? flow.destinationSiteId : flow.sourceSiteId
    const siteName = grouping.isPickup ? flow.destinationSiteName : flow.sourceSiteName
    if (siteId && siteName) {
      const reverseOptions: SortAndCalcOptions = {id: siteId, name: siteName, entityType: "SITE"}
      const reverseGrouping = this.getOrComputeGroupingRecord(grouping.reverseSiteStatMap, reverseOptions)
      this.calculateFlowGroupingStats(ts, flow, reverseGrouping)
    }

    if (grouping.entityType === 'PIPE' && grouping.isPickup) {
      const siteId = flow.sourceSiteId
      const siteName = flow.sourceSiteName
      if (siteId && siteName) {
        const childOptions: SortAndCalcOptions = {id: siteId, name: siteName, entityType: "SITE", pickup: true}
        const childGrouping = this.getOrComputeGroupingRecord(grouping.childMap, childOptions)
        this.calculateFlowGroupingStats(ts, flow, childGrouping)
      }
    }

    if (options.calcSubPipelines) {
      const pipelineId = flow.pipelineId
      const pipelineName = flow.pipelineName

      if (pipelineId && pipelineName) {
        let subOptions: SortAndCalcOptions = {
          id: pipelineId,
          name: pipelineName,
          entityType: "PIPE",
          pickup: grouping.isPickup
        }
        let subGrouping = this.getOrComputeGroupingRecord(grouping.pipelineMap, subOptions)
        this.calculateFlowGroupingStats(ts, flow, subGrouping)

        const siteId = grouping.isPickup ? flow.sourceSiteId : flow.destinationSiteId
        const siteName = grouping.isPickup ? flow.sourceSiteName : flow.destinationSiteName
        if (siteId && siteName) {
          subOptions = {
            id: siteId,
            name: siteName,
            entityType: "SITE",
            pickup: grouping.isPickup
          }
          subGrouping = this.getOrComputeGroupingRecord(subGrouping.siteMap, subOptions)
          this.calculateFlowGroupingStats(ts, flow, subGrouping)
        }
      }
    }

    const dttm = grouping.isPickup ? flow?.dttm : flow?.endDttm
    if (dttm) {
      const hourDttm = adjustToTimeZone(startOfHour(adjustFromTimeZone(dttm))).getTime()
      const dayDttm = adjustToTimeZone(startOfDay(adjustFromTimeZone(dttm))).getTime()
      const weekDttm = adjustToTimeZone(startOfWeek(adjustFromTimeZone(dttm))).getTime()
      const monthDttm = adjustToTimeZone(startOfMonth(adjustFromTimeZone(dttm))).getTime()

      let timeStats: TimeStatisticsWrapper = this.getOrComputeTimeGrouping(hourDttm, grouping.hour)
      this.calculateFlowGroupingStats(ts, flow, timeStats)

      timeStats = this.getOrComputeTimeGrouping(dayDttm, grouping.day)
      this.calculateFlowGroupingStats(ts, flow, timeStats)

      timeStats = this.getOrComputeTimeGrouping(weekDttm, grouping.week)
      this.calculateFlowGroupingStats(ts, flow, timeStats)

      timeStats = this.getOrComputeTimeGrouping(monthDttm, grouping.month)
      this.calculateFlowGroupingStats(ts, flow, timeStats)
    }
  }

  private calculateFlowGroupingStats = (ts: TransportServices, flow: PipelineFlowTransport, grouping: StatisticsGroupingWrapper) => {
    let categoryStats: PipelineCategoryStatisticsWrapper = grouping.pipeline.other
    if (flow.category) {
      if (flow.category === transportCategory.water.code) {
        categoryStats = grouping.pipeline.water
      } else if (flow.category === transportCategory.oil.code) {
        categoryStats = grouping.pipeline.oil
      }
    }

    // increment record count
    categoryStats.recordCount++;

    // cost
    if (flow.objectCostRateModels) {
      for (const ocrm of flow.objectCostRateModels) {
        if (this.shouldAddCost(ocrm)) {
          categoryStats.addOcrmCost(ocrm);
        }
      }
    }

    // quantity
    let quantity = flow.grossQuantity
    const uom: string | undefined = flow.unitOfMeasure;
    if (quantity && uom) {
      const convertedQuantity = convertVolumeQuantity(
        uom,
        this.volumeUnitOfMeasure,
        quantity
      );
      if (convertedQuantity) {
        categoryStats.totalQty += convertedQuantity;
      }
    }

    // standard quantity
    quantity = flow.standardQuantity ?? flow.grossQuantity
    if (quantity && uom) {
      const convertedQuantity = convertVolumeQuantity(
        uom,
        this.volumeUnitOfMeasure,
        quantity
      );
      if (convertedQuantity) {
        categoryStats.totalStandardQty += convertedQuantity;
      }
    }

    // net quantity
    quantity = flow.netQuantity ?? flow.grossQuantity
    if (quantity && uom) {
      const convertedQuantity = convertVolumeQuantity(
        uom,
        this.volumeUnitOfMeasure,
        quantity
      );
      if (convertedQuantity) {
        categoryStats.totalNetQty += convertedQuantity;
      }
    }

    // last activity dates
    const recordDttm = flow.endDttm ?? flow.dttm
    if (recordDttm) {
      const lastActivityDttm = categoryStats.lastActivityDttm;
      if (!lastActivityDttm || recordDttm > lastActivityDttm) {
        categoryStats.lastActivityDttm = recordDttm;
      }
    }
  }

  private shouldAddCost = (ocrm: ObjectCostRateModel): boolean => {
    return (
      typeof ocrm.totalCost !== 'undefined' &&
      (ocrm.rateModelType === 'Disposal' ||
        (ocrm.rateModelType === 'Transport' &&
          ocrm.rateModelCompanyType === companyType.truckCompany.code))
    );
  };

  private getOrComputeTimeGrouping = (dttm: number, bucket: Record<number, TimeStatisticsWrapper>): TimeStatisticsWrapper => {
    let timeStats: TimeStatisticsWrapper
    if (dttm in bucket) {
      timeStats = bucket[dttm]
    } else {
      timeStats = new TimeStatisticsWrapper(dttm)
      bucket[dttm] = timeStats
    }
    return timeStats
  }

  private getOrComputeGrouping = (sortMap: Map<string, StatisticsGroupWrapper>, options: SortAndCalcOptions) => {
    const name = options.name
    let grouping = sortMap.get(name);
    if (!grouping) {
      grouping = new StatisticsGroupWrapper(options.id, name, options.entityType);
      grouping.uom = this.volumeUnitOfMeasure;
      grouping.timeUom = this.timeUnitOfMeasure
      grouping.isPickup = options.pickup ?? false
      sortMap.set(name, grouping);
    }
    return grouping
  }

  private getOrComputeGroupingRecord = (sortMap: Record<string, StatisticsGroupWrapper>, options: SortAndCalcOptions) => {
    const name = options.name
    let grouping = sortMap[name]
    if (!grouping) {
      grouping = new StatisticsGroupWrapper(options.id, name, options.entityType);
      grouping.uom = this.volumeUnitOfMeasure;
      grouping.timeUom = this.timeUnitOfMeasure
      grouping.isPickup = options.pickup ?? false
      sortMap[name] = grouping;
    }
    return grouping
  }

  private getTtaTruckCompany = (tta: TransportAssoc): {
    id: string | undefined,
    name: string | undefined
  } => {
    const transport = tta.pickupTransport ?? tta.deliveryTransport
    return {
      id: transport?.parentCompanyId ?? transport?.trailerCompanyId,
      name: transport?.parentCompanyName ?? transport?.trailerCompanyName
    }
  }

  private getTtaVendorCompany = (tta: TransportAssoc): {
    id: string | undefined,
    name: string | undefined
  } => {
    let id: string | undefined = undefined
    let name: string | undefined = undefined;

    if (tta.pickupTransport?.siteCompanyType === companyType.vendor.code) {
      id = tta.pickupTransport.siteCompanyId;
      name = tta.pickupTransport.siteCompanyName;
    } else if (tta.deliveryTransport?.siteCompanyType === companyType.vendor.code) {
      id = tta.deliveryTransport.siteCompanyId;
      name = tta.deliveryTransport.siteCompanyName;
    }

    return {
      id: id,
      name: name
    }
  }

  private getTtaOperatorCompany = (tta: TransportAssoc): {
    id: string | undefined,
    name: string | undefined
  } => {
    let id: string | undefined = undefined
    let name: string | undefined = undefined;

    if (tta.pickupTransport?.siteCompanyType === companyType.operator.code) {
      id = tta.pickupTransport.siteCompanyId;
      name = tta.pickupTransport.siteCompanyName;
    } else if (tta.deliveryTransport?.siteCompanyType === companyType.operator.code) {
      id = tta.deliveryTransport.siteCompanyId;
      name = tta.deliveryTransport.siteCompanyName;
    }

    return {
      id: id,
      name: name
    }
  }

  private getFlowVendorCompany = (flow: PipelineFlowTransport): {
    id: string | undefined,
    name: string | undefined
  } => {
    let id: string | undefined = undefined
    let name: string | undefined = undefined;

    if (flow.destinationSiteCompanyType === companyType.vendor.code) {
      id = flow.destinationSiteCompanyId
      name = flow.destinationSiteCompanyName
    } else if (flow.sourceSiteCompanyType === companyType.vendor.code) {
      id = flow.sourceSiteCompanyId
      name = flow.sourceSiteCompanyName
    }

    return {
      id: id,
      name: name
    }
  }

  private getFlowOperatorCompany = (flow: PipelineFlowTransport): {
    id: string | undefined,
    name: string | undefined
  } => {
    let id: string | undefined = undefined
    let name: string | undefined = undefined;

    if (flow.destinationSiteCompanyType === companyType.operator.code) {
      id = flow.destinationSiteCompanyId
      name = flow.destinationSiteCompanyName
    } else if (flow.sourceSiteCompanyType === companyType.operator.code) {
      id = flow.sourceSiteCompanyId
      name = flow.sourceSiteCompanyName
    }

    return {
      id: id,
      name: name
    }
  }
}
